'use client';

import { useState } from 'react';
import { ChevronsUpDown, ExternalLink, LogOut, Smartphone } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuthClient } from '@/lib/authClient';
import denApi from '@/lib/api/denApi';
import { formatName } from '@/lib/name';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { signOut } = useAuthClient();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const resourceLinks = [
    {
      label: 'Documentation',
      href: 'https://docs.mls.onchainden.com/',
    },
    {
      label: 'Terms',
      href: 'https://onchainden.com/terms',
    },
    {
      label: 'Privacy',
      href: 'https://onchainden.com/privacy',
    },
  ] as const;

  const disconnectMobileSession = async () => {
    try {
      await denApi.post('user/disconnect-mobile-session', {});
      // Force reload to clear any cached mobile session data
      window.location.reload();
    } catch (error) {
      console.error('Failed to disconnect mobile session:', error);
      // Optionally add a toast notification here
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground pl-4"
            >
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {formatName(user.name)}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2 py-1.5 text-left text-sm">
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open documentation and legal links"
                      className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-transparent p-0 text-popover-foreground outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                    >
                      <span
                        aria-hidden="true"
                        className="text-[13px] font-semibold leading-none"
                      >
                        ?
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-48 min-w-48 rounded-lg"
                    side={isMobile ? 'bottom' : 'right'}
                    align="end"
                    sideOffset={6}
                  >
                    {resourceLinks.map((link) => (
                      <DropdownMenuItem
                        key={link.href}
                        asChild
                        className="cursor-pointer"
                      >
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`${link.label} (opens in a new tab)`}
                        >
                          <ExternalLink className="text-muted-foreground" />
                          {link.label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                setIsDisconnectDialogOpen(true);
              }}
            >
              <Smartphone />
              Disconnect mobile session
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                signOut();
              }}
            >
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect mobile session</DialogTitle>
            <DialogDescription>
              This will end the current mobile session and require a new pairing
              to reconnect the device.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDisconnectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await disconnectMobileSession();
              }}
            >
              End session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarMenu>
  );
}
